import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { complianceApi } from '@/api/compliance.api';

const SANDBOX_STATE_ACCOUNTS = [
  { state: 'Maharashtra (27)', gstin: '27AAGCB1286Q005', user: 'BVMMH', pass: 'Bvm@123456' },
  { state: 'Karnataka (29)', gstin: '29AAGCB1286Q000', user: 'BVMGSP', pass: 'Wbooks@0142' },
  { state: 'Delhi (07)', gstin: '07AAGCB1286Q012', user: 'BVMDL', pass: 'Bvm@123456' },
  { state: 'Gujarat (24)', gstin: '24AAGCB1286Q029', user: 'BVMGJ', pass: 'Bvm@123456' },
  { state: 'Uttar Pradesh (09)', gstin: '09AAGCB1286Q014', user: 'BVMUP', pass: 'Bvm@123456' },
];

export default function EWayBillSettingsPage() {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [threshold, setThreshold] = useState('50000');
  const [alertBefore, setAlertBefore] = useState('6');
  const [defaultMode, setDefaultMode] = useState('1');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [apiUsername, setApiUsername] = useState('BVMMH');
  const [apiPassword, setApiPassword] = useState('Bvm@123456');
  const [apiEmail, setApiEmail] = useState('');
  const [gstin, setGstin] = useState('27AAGCB1286Q005');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [serverIp, setServerIp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [emailAlert, setEmailAlert] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [connectionTesting, setConnectionTesting] = useState(false);

  const whitebooksUrl = environment === 'sandbox'
    ? 'https://sandbox.whitebooks.in/ewaybillapi/v1.03'
    : 'https://api.whitebooks.in/ewaybillapi/v1.03';

  // Load Settings from API on Mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const res = await complianceApi.getEWayBillSettings();
        if (res?.success && res?.data) {
          const d = res.data;
          setAutoGenerate(Boolean(d.autoGenerateEwb));
          setThreshold(String(d.ewbThreshold || '50000'));
          setAlertBefore(String(d.alertBeforeHours || '6'));
          setDefaultMode(String(d.defaultTransportMode || '1'));
          setEnvironment(d.environment === 'production' ? 'production' : 'sandbox');
          setApiUsername(d.apiUsername || 'BVMMH');
          setApiPassword(d.apiPassword || 'Bvm@123456');
          setApiEmail(d.apiEmail || '');
          setGstin(d.gstin || '27AAGCB1286Q005');
          setClientId(d.clientId || '');
          setClientSecret(d.clientSecret || '');
          setServerIp(d.serverIp || '');
          setEmailAlert(Boolean(d.emailAlertsEnabled));
          setEmailRecipients(d.emailRecipients || '');
        }
      } catch (err) {
        console.error('Failed to load EWB settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSelectAccount = (acc: typeof SANDBOX_STATE_ACCOUNTS[0]) => {
    setGstin(acc.gstin);
    setApiUsername(acc.user);
    setApiPassword(acc.pass);
    toast.info(`Selected ${acc.state} sandbox account`);
  };

  const handleTestConnection = async () => {
    setConnectionTesting(true);
    try {
      const res = await complianceApi.testEWayBillAuth({
        gstin,
        username: apiUsername,
        password: apiPassword,
        clientId,
        clientSecret,
        environment,
        email: apiEmail || emailRecipients?.split(',')[0]?.trim(),
        serverIp: serverIp || undefined,
      });
      if (res?.success) {
        toast.success(res.message || 'Authenticated successfully with Whitebooks GSP API ✓');
      } else {
        toast.error(res?.message || 'Authentication failed');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Authentication test failed');
    } finally {
      setConnectionTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await complianceApi.updateEWayBillSettings({
        autoGenerateEwb: autoGenerate,
        ewbThreshold: Number(threshold),
        alertBeforeHours: Number(alertBefore),
        defaultTransportMode: defaultMode,
        environment,
        apiEmail,
        apiUsername,
        apiPassword,
        clientId,
        clientSecret,
        gstin,
        serverIp,
        emailAlertsEnabled: emailAlert,
        emailRecipients,
      });

      if (res?.success) {
        toast.success('E-Way Bill Settings Saved Successfully ✓');
      } else {
        toast.error(res?.message || 'Failed to save settings');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2 text-slate-500">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm font-medium">Loading E-Way Bill Settings...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">E-Way Bill & Whitebooks API Settings</h1>
            <p className="text-sm text-slate-500 mt-0.5">Configure E-Way Bill generation threshold and Whitebooks GSP API credentials</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-[#4338ca] disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
            Save Settings
          </button>
        </div>

        {/* Section 1: General */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">General Configuration</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Auto-generate EWB on invoice save</p>
                <p className="text-xs text-gray-400 mt-0.5">Auto-suggest when invoice total &gt; ₹{Number(threshold).toLocaleString('en-IN')}</p>
              </div>
              <button
                onClick={() => setAutoGenerate(!autoGenerate)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${autoGenerate ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoGenerate ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">EWB Mandatory Threshold Amount (₹)</label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
              <p className="mt-0.5 text-[11px] text-gray-400">Goods movement above this invoice value legally requires E-Way Bill</p>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Alert before expiry</label>
              <select
                value={alertBefore}
                onChange={(e) => setAlertBefore(e.target.value)}
                className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white"
              >
                <option value="2">2 hours before expiration</option>
                <option value="4">4 hours before expiration</option>
                <option value="6">6 hours before expiration</option>
                <option value="12">12 hours before expiration</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Default Transport Mode</label>
              <select
                value={defaultMode}
                onChange={(e) => setDefaultMode(e.target.value)}
                className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white"
              >
                <option value="1">Road (1)</option>
                <option value="2">Rail (2)</option>
                <option value="3">Air (3)</option>
                <option value="4">Ship (4)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: Whitebooks GSTN API Credentials */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Whitebooks GSP API Credentials</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-600 mb-2">Environment</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEnvironment('sandbox')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border cursor-pointer whitespace-nowrap ${environment === 'sandbox' ? 'bg-amber-50 text-amber-700 border-amber-300 font-semibold' : 'bg-white text-gray-600 border-gray-300'}`}
                >
                  Sandbox (Test Mode)
                </button>
                <button
                  onClick={() => setEnvironment('production')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border cursor-pointer whitespace-nowrap ${environment === 'production' ? 'bg-red-50 text-red-700 border-red-300 font-semibold' : 'bg-white text-gray-600 border-gray-300'}`}
                >
                  Production (Live GST Portal)
                </button>
              </div>
              {environment === 'sandbox' && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-2">
                  <div className="flex items-center gap-1 font-semibold">
                    <i className="ri-information-line" /> Whitebooks Sandbox Test Credentials:
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {SANDBOX_STATE_ACCOUNTS.map((acc) => (
                      <button
                        key={acc.state}
                        type="button"
                        onClick={() => handleSelectAccount(acc)}
                        className={`text-left p-2 rounded border transition-colors cursor-pointer text-[11px] ${gstin === acc.gstin ? 'bg-amber-100 border-amber-400 font-semibold' : 'bg-white border-amber-200 hover:bg-amber-100/50'}`}
                      >
                        <div className="font-bold text-amber-900">{acc.state}</div>
                        <div>GSTIN: {acc.gstin}</div>
                        <div>User: {acc.user}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Supplier GSTIN Number</label>
                <input
                  type="text"
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  placeholder="e.g. 27AAGCB1286Q005"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] uppercase font-mono"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">API Username</label>
                <input
                  type="text"
                  value={apiUsername}
                  onChange={(e) => setApiUsername(e.target.value)}
                  placeholder="Enter GSP API username"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] font-mono"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">API Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={apiPassword}
                    onChange={(e) => setApiPassword(e.target.value)}
                    placeholder="Enter GSP API password"
                    className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Whitebooks Registered Email</label>
                <input
                  type="email"
                  value={apiEmail}
                  onChange={(e) => setApiEmail(e.target.value)}
                  placeholder="email@example.com (used for API authentication)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
                />
                <p className="mt-0.5 text-[11px] text-gray-400">The email registered with Whitebooks GSP for this GSTIN</p>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Client ID</label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="EWBSxxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] font-mono"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Client Secret</label>
                <div className="relative">
                  <input
                    type={showClientSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="EWBSxxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showClientSecret ? 'ri-eye-line' : 'ri-eye-off-line'} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Whitebooks API Gateway URL</label>
              <input
                type="text"
                readOnly
                value={whitebooksUrl}
                className="w-full max-w-lg px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Server IP Address <span className="text-gray-400 font-normal">(optional override)</span>
              </label>
              <input
                type="text"
                value={serverIp}
                onChange={(e) => setServerIp(e.target.value)}
                placeholder="Auto-detected from system (e.g. 103.45.67.89)"
                className="w-full max-w-lg px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] font-mono"
              />
              <p className="mt-0.5 text-[11px] text-gray-400">
                Leave blank to auto-detect. For production VPS, enter the public IP whitelisted with Whitebooks.
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={connectionTesting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#4f46e5] bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {connectionTesting ? (
                  <><i className="ri-loader-4-line animate-spin" /> Authenticating...</>
                ) : (
                  <><i className="ri-link" /> Test Whitebooks API Connection</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Button Footer */}
        <div className="flex justify-end pt-2 pb-8">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-[#4338ca] disabled:opacity-50 cursor-pointer flex items-center gap-2 shadow-sm"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
            Save EWB Settings
          </button>
        </div>
      </div>
    </AppLayout>
  );
}